import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Stack, Switch, TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import iziToast from 'izitoast';

import { createLesson, deleteLesson, getGroup, updateLesson } from '../../actions/journal';
import { internalServerError } from '../../utils/consts/pathRoutes';
import useIsMobile, { mobileDialogProps } from '../../utils/useIsMobile';
import { currentMonth, formatDate, todayString } from '../../utils/journalFormat';

/**
 * Создание и правка занятия.
 *
 * Занятие — это пара, а не день: у одного дня может быть несколько занятий с разными
 * преподавателями и разными плановыми часами, номер пары их и различает.
 * Правка нужна не реже создания: часы в расписании меняются, а преподаватель
 * подставляется задним числом.
 */
const LessonModal = ({ state, groupId, month, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const open = Boolean(state);
  const isEdit = state?.mode === 'edit';
  const lesson = state?.lesson;

  const [teachers, setTeachers] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // текст предупреждения перед удалением с отметками

  useEffect(() => {
    if (!open) return;

    // Предупреждение живёт только внутри одного открытия окна: иначе оно оставалось
    // висеть на следующем занятии и даже на создании нового.
    setConfirm(null);

    setForm(isEdit ? {
      lesson_date: lesson.lesson_date,
      slot: lesson.slot,
      planned_hours: lesson.planned_hours,
      teacher_id: lesson.teacher_id || '',
      is_restored: Boolean(lesson.is_restored),
      restored_for_date: lesson.restored_for_date || '',
      comment: lesson.comment || '',
    } : {
      // Сегодня, если открытый месяц текущий; иначе первое число открытого месяца —
      // занятие заводят за тот период, который смотрят.
      lesson_date: month === currentMonth() ? todayString() : `${month}-01`,
      slot: 1,
      planned_hours: 4,
      teacher_id: '',
      is_restored: false,
      restored_for_date: '',
      comment: '',
    });

    getGroup(groupId)
      .then((group) => setTeachers(group.teachers.filter((teacher) => teacher.is_active)))
      .catch(() => setTeachers([]));
  }, [open, isEdit, lesson, groupId, month]);

  if (!open || !form) return null;

  const change = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const payload = () => ({
    ...form,
    teacher_id: form.teacher_id || null,
    restored_for_date: form.is_restored ? form.restored_for_date || null : null,
  });

  const submit = () => {
    setSaving(true);

    const request = isEdit
      ? updateLesson(groupId, lesson.id, payload())
      : createLesson(groupId, payload());

    request
      .then(() => {
        iziToast.success({
          message: isEdit ? 'Занятие изменено' : 'Занятие добавлено',
          position: 'topRight',
        });
        onSaved();
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setSaving(false));
  };

  /**
   * Удаление занятия в два шага.
   *
   * Заполненный столбец удалить было нельзя вообще: сервер отказывал, если по занятию
   * есть отметки, а чистить их по одной у каждого слушателя — работа на полчаса.
   * Теперь сервер сообщает, сколько отметок потеряется, и мы переспрашиваем.
   */
  const remove = (force = false) => {
    setSaving(true);
    deleteLesson(groupId, lesson.id, force)
      .then(() => {
        iziToast.success({ message: 'Занятие удалено', position: 'topRight' });
        onSaved();
      })
      .catch((e) => {
        if (e?.response?.status === 409) {
          setConfirm(e.response.data?.message || 'У занятия есть отметки');
          return;
        }
        iziToast.error({ message: internalServerError(e), position: 'topRight' });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth {...mobileDialogProps(isMobile)}>
      <DialogTitle>
        {isEdit ? `Занятие ${formatDate(lesson.lesson_date)}, пара ${lesson.slot}` : 'Новое занятие'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Дата" type="date" value={form.lesson_date}
              onChange={change('lesson_date')} InputLabelProps={{ shrink: true }} fullWidth
            />
            <TextField
              label="Пара" type="number" value={form.slot}
              onChange={change('slot')} sx={{ width: 90 }}
            />
          </Stack>

          <TextField
            label="Плановых часов" type="number" value={form.planned_hours}
            onChange={change('planned_hours')}
            autoFocus={isEdit}
          />

          <TextField
            select label="Преподаватель" value={form.teacher_id} onChange={change('teacher_id')}
            helperText={teachers.length === 0
              ? 'У группы пока нет преподавателей — они заводятся в базе данных'
              : 'Его инициалы будут подписаны в шапке столбца'}
          >
            <MenuItem value="">Не указан</MenuItem>
            {teachers.map((teacher) => (
              <MenuItem key={teacher.user_id} value={teacher.user_id}>{teacher.name}</MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={(
              <Switch
                checked={form.is_restored}
                onChange={(event) => setForm((prev) => ({ ...prev, is_restored: event.target.checked }))}
              />
            )}
            label="Восстановленная пара"
          />

          {form.is_restored && (
            <TextField
              label="За какую дату" type="date" value={form.restored_for_date}
              onChange={change('restored_for_date')} InputLabelProps={{ shrink: true }}
            />
          )}

          {isEdit && !confirm && (
            <Alert severity="info">
              Занятие с отметками удаляется вместе с ними — система сначала переспросит.
            </Alert>
          )}

          {confirm && (
            <Alert severity="warning">
              {confirm}. Нажмите «Удалить» ещё раз, чтобы подтвердить.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      {/* «Удалить с отметками», «Отмена» и «Сохранить» на телефоне в строку не встают. */}
      <DialogActions sx={isMobile ? { flexWrap: 'wrap', rowGap: 1 } : undefined}>
        {isEdit && (
          <Button
            size="small" startIcon={<DeleteIcon />} disabled={saving}
            variant={confirm ? 'outlined' : 'text'}
            onClick={() => remove(Boolean(confirm))}
            sx={{ color: '#b0504a', borderColor: '#d8a9a5' }}
          >
            {confirm ? 'Удалить с отметками' : 'Удалить'}
          </Button>
        )}
        <Button onClick={onClose} sx={{ ml: 'auto' }}>Отмена</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !form.lesson_date}>
          {isEdit ? 'Сохранить' : 'Добавить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LessonModal;
